// Frontend/src/pages/fit/FitCreateAccount.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebaseConfig";

const FitCreateAccount = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    company: '',
    role: 'Revendeur',
    phone: '',
    address: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return false;
    }
    
    if (formData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return false;
    }
    
    if (!formData.email || !formData.password || !formData.nom || !formData.prenom) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return false;
    }
    
    return true;
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      nom: '',
      prenom: '',
      company: '',
      role: 'Revendeur',
      phone: '',
      address: ''
    });
  };

  // ✅ NOUVELLE FONCTION: Vérification email via API backend
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const checkEmailExists = async (email) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/check-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ email })
      });
  
      if (!res.ok) {
        throw new Error("Erreur API: " + res.status);
      }
  
      const data = await res.json();
      return data;
    } catch (err) {
      console.error("Erreur vérification email:", err);
      throw err;
    }
  };

  // ✅ NOUVELLE FONCTION: Création compte via API backend
  const createAccountViaAPI = async (email, password, userData) => {
    try {
      // 1. Vérifier email
      const emailCheck = await checkEmailExists(email);
      
      if (emailCheck.exists) {
        return {
          success: false,
          type: emailCheck.isApproved ? "APPROVED_ACCOUNT_EXISTS" : "PENDING_ACCOUNT_EXISTS",
          existingUser: emailCheck.userData,
          userId: emailCheck.userId,
          message: emailCheck.message
        };
      }

      if (emailCheck.error) {
        return {
          success: false,
          type: "VALIDATION_ERROR",
          message: emailCheck.message
        };
      }

      // 2. Créer compte Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 3. Créer document via API backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/create-user-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          uid: user.uid,
          userData: {
            ...userData,
            email: email.toLowerCase(),
            createdAt: new Date().toISOString(),
            isApproved: true, // ✅ TOUS les comptes créés par FIT sont automatiquement approuvés
            createdBy: "FIT Admin",
            uid: user.uid
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API création document: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // 4. Définir les custom claims
        try {
          const claimsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/custom-claims/setCustomClaims`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              uid: user.uid,
              role: userData.role,
              isApproved: true,
            }),
          });

          const claimsResult = await claimsResponse.json();
          console.log("✅ Claims définies:", claimsResult);
        } catch (claimsError) {
          console.warn("Erreur claims:", claimsError);
        }

        return {
          success: true,
          message: "Compte créé avec succès ! L'utilisateur peut se connecter immédiatement.",
          userId: user.uid
        };
      } else {
        throw new Error(result.error || 'Erreur création document');
      }

    } catch (error) {
      console.error("Erreur création compte:", error);
      
      // Gestion des erreurs spécifiques Firebase
      switch (error.code) {
        case "auth/email-already-in-use":
          return {
            success: false,
            type: "EMAIL_IN_USE",
            message: "Cet email est déjà utilisé par un autre compte."
          };
        case "auth/weak-password":
          return {
            success: false,
            type: "WEAK_PASSWORD", 
            message: "Le mot de passe doit contenir au moins 6 caractères."
          };
        case "auth/invalid-email":
          return {
            success: false,
            type: "INVALID_EMAIL",
            message: "Format d'email invalide."
          };
        default:
          return {
            success: false,
            type: "UNKNOWN_ERROR",
            message: error.message || "Erreur lors de la création du compte."
          };
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      const userData = {
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        company: formData.company.trim(),
        role: formData.role,
        phone: formData.phone.trim(),
        address: formData.address.trim()
      };

       // 🔥 DEBUG : Afficher la valeur du rôle
    console.log("🔍 ROLE ENVOYÉ:", formData.role);
    console.log("🔍 USER DATA:", userData);

      // ✅ UTILISER LA NOUVELLE FONCTION API
      const result = await createAccountViaAPI(
        formData.email.trim(),
        formData.password,
        userData
      );

      if (result.success) {
        toast.success(result.message);
        resetForm();
        // Rediriger vers la gestion des comptes après 2 secondes
        setTimeout(() => {
          navigate('/fit/manage-accounts');
        }, 2000);
      } else {
        // ✅ PLUS DE GESTION "EN ATTENTE" - Tout compte existant = erreur simple
        if (result.type === "PENDING_ACCOUNT_EXISTS" || result.type === "APPROVED_ACCOUNT_EXISTS") {
          toast.error("Ce compte existe déjà. Veuillez utiliser un autre email.");
        } else {
          toast.error(result.message);
        }
      }
    } catch (error) {
      console.error("Erreur création compte:", error);
      toast.error("Erreur lors de la création du compte");
    } finally {
      setLoading(false);
    }
  };

  const handleConflictSuccess = () => {
    toast.success("Compte traité avec succès !");
    resetForm();
    setTimeout(() => {
      navigate('/fit/manage-accounts');
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Créer un nouveau compte
              </h1>
              <p className="text-gray-600 mt-1">
                Ajouter un utilisateur au système FIT Doors
              </p>
            </div>
            <button
              onClick={() => navigate('/fit/manage-accounts')}
              className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Retour
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Informations personnelles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Informations personnelles
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    name="prenom"
                    required
                    value={formData.prenom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Jean"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="nom"
                    required
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Dupont"
                  />
                </div>
              </div>
            </div>

            {/* Informations de contact */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Informations de contact
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="jean.dupont@exemple.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    L'email sera vérifié automatiquement pour éviter les doublons
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>
            </div>

            {/* Informations professionnelles */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Informations professionnelles
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Nom de l'entreprise"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rôle *
                  </label>
                  <select
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-colors"
                  >
                    <option value="Revendeur">Revendeur</option>
                    <option value="Carrossier">Carrossier</option>
                    <option value="client">Client</option>
                    <option value="fit">FIT (Administrateur)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Le rôle détermine les permissions dans l'application
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                    placeholder="Adresse complète de l'entreprise"
                  />
                </div>
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Sécurité
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      required
                      minLength="6"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Au moins 6 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Ressaisir le mot de passe"
                  />
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Les mots de passe ne correspondent pas
                    </p>
                  )}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && formData.password.length >= 6 && (
                    <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Mots de passe identiques
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Information sur le processus */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="font-medium text-green-900 mb-1">Création automatique</h4>
                  <p className="text-sm text-green-700">
                    Tous les comptes créés via cette interface sont automatiquement approuvés. L'utilisateur pourra se connecter immédiatement.
                  </p>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/fit/manage-accounts')}
                className="flex-1 px-6 py-3 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading || (formData.password && formData.password !== formData.confirmPassword)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Création...
                  </div>
                ) : (
                  "Créer le compte"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FitCreateAccount;